import theme from "@/styles/theme";
import { IconButton, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import {
  DeleteOutlined as DeleteOutlinedIcon,
  ModeEditOutlineOutlined as ModeEditOutlineOutlinedIcon
} from "@mui/icons-material";
import pdficon from "../../../assets/icons/pdficon.png";

/**
 * Compara dois valores para ordenação descendente.
 */
export const descendingComparator = <T,>(
  a: T,
  b: T,
  orderBy: keyof T
): number => {
  const aVal = a[orderBy];
  const bVal = b[orderBy];

  if (typeof aVal === "number" && typeof bVal === "number") {
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  }

  if (typeof aVal === "string" && typeof bVal === "string") {
    const aDate = new Date(aVal);
    const bDate = new Date(bVal);
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      if (bDate.getTime() < aDate.getTime()) return -1;
      if (bDate.getTime() > aDate.getTime()) return 1;
      return 0;
    }
  }

  const aStr = String(aVal);
  const bStr = String(bVal);
  if (bStr < aStr) return -1;
  if (bStr > aStr) return 1;
  return 0;
};

export type Order = "asc" | "desc";

export const getComparator = <Key extends keyof any>(
  order: Order,
  orderBy: Key
) => {
  return order === "desc"
    ? (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string }
      ) => descendingComparator(a, b, orderBy)
    : (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string }
      ) => -descendingComparator(a, b, orderBy);
};

/**
 * Formata um texto contendo blocos de parágrafo separados por "\n\n" e
 * torna partes entre *asteriscos* em <strong>.
 */
export const formatText = (input: string) =>
  input.split("\n\n").map((paragraph, index) => {
    const parts = paragraph.split(/(\*[^*]+\*)/g);
    return (
      <p
        key={index}
        style={{
          marginBottom: "1.5vh",
          textAlign: "justify",
          marginRight: "10px"
        }}
      >
        {parts.map((part, partIndex) =>
          /^\*[^*]+\*$/.test(part) ? (
            <strong key={partIndex}>{part.slice(1, -1)}</strong>
          ) : (
            part
          )
        )}
      </p>
    );
  });

/**
 * Converte uma string ISO em "dd/LL/yyyy HH:mm" (pt-BR). Se inválida, retorna o valor original.
 */
export const formatDateTime = (value: string): string => {
  const dt = DateTime.fromISO(value);
  return dt.isValid
    ? dt.setLocale("pt-BR").toFormat("dd/LL/yyyy HH:mm")
    : value;
};

/**
 * Renderiza o ícone de edição.
 * @param row   - dados da linha
 * @param handleEdit - callback (row) => void
 */
export const renderEdit = (
  row: any,
  handleEdit: ((item: any, index?: number) => void) | undefined
) => (
  <Tooltip title="Editar" arrow placement="top">
    <IconButton size="small" onClick={() => handleEdit!(row)}>
      <ModeEditOutlineOutlinedIcon
        sx={{ color: theme.colors.palete.brownDark }}
      />
    </IconButton>
  </Tooltip>
);

/**
 * Renderiza o ícone para baixar PDF.
 * @param row   - dados da linha
 * @param handleDownload - callback (row) => void
 */
export const renderDownload = (
  row: any,
  handleDownload: ((item: any, index?: number) => void) | undefined
) => (
  <Tooltip title="Baixar registro" arrow placement="top">
    <img
      src={pdficon}
      onClick={() => handleDownload!(row)}
      style={{ width: "30px", cursor: "pointer" }}
    />
  </Tooltip>
);

/**
 * Renderiza o ícone de exclusão.
 * @param row   - dados da linha
 * @param setItemToDelete - callback para setar o item antes da exclusão
 * @param toggleModalDelete - callback para abrir caixa de confirmação
 */
export const renderDelete = (
  row: any,
  setItemToDelete: (value: any) => void,
  toggleModalDelete: () => void
) => (
  <Tooltip title="Deletar" arrow placement="top">
    <IconButton
      size="small"
      onClick={() => {
        setItemToDelete(row);
        toggleModalDelete();
      }}
    >
      <DeleteOutlinedIcon sx={{ color: theme.colors.palete.brownDark }} />
    </IconButton>
  </Tooltip>
);
